/*
Copyright 2022-2024 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import {
  type FC,
  type MouseEvent,
  useCallback,
  useMemo,
  useState,
  useEffect,
} from "react";
import { useTranslation } from "react-i18next";
import { type Room } from "matrix-js-sdk";
import { Button, Text } from "@vector-im/compound-web";
import {
  LinkIcon,
  CheckIcon,
} from "@vector-im/compound-design-tokens/assets/web/icons";
import copy from "copy-to-clipboard";

import { Modal } from "../Modal";
import { getAbsoluteRoomUrl } from "../utils/matrix";
import styles from "./InviteModal.module.css";
import { Toast } from "../Toast";
import { useRoomEncryptionSystem } from "../e2ee/sharedKeyManagement";
import { QrCode } from "../QrCode";

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || "";

interface Props {
  room: Room;
  open: boolean;
  onDismiss: () => void;
}

export const InviteModal: FC<Props> = ({ room, open, onDismiss }) => {
  const { t } = useTranslation();
  const e2eeSystem = useRoomEncryptionSystem(room.roomId);

  const url = useMemo(
    () => getAbsoluteRoomUrl(room.roomId, e2eeSystem, room.name),
    [e2eeSystem, room.name, room.roomId],
  );

  const [shortUrl, setShortUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (open && url) {
      let active = true;
      setIsLoading(true);
      setShortUrl(null); 
      
      fetch(`${BACKEND_URL}/api/shorten`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ originalUrl: url })
      })
      .then(res => res.json())
      .then(data => {
        if (active && data.shortUrl) {
          setShortUrl(data.shortUrl);
        }
      })
      .catch(err => {
        console.error("Ошибка шортлинка:", err);
        // В случае ошибки (упал бэкенд) отдаем длинную ссылку как запасной вариант
        if (active) setShortUrl(url); 
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });

      return () => { active = false; };
    }
  }, [open, url]);

  const displayUrl = shortUrl || url;

  const [toastOpen, setToastOpen] = useState(false);
  const onToastDismiss = useCallback(() => setToastOpen(false), [setToastOpen]);

  const onButtonClick = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      copy(displayUrl); 
      onDismiss();
      setToastOpen(true);
    },
    [displayUrl, onDismiss], 
  );

  return (
    <>
      <Modal title={t("invite_modal.title")} open={open} onDismiss={onDismiss}>
        {isLoading ? (
          /* КРАСИВАЯ ЗАГЛУШКА (СКЕЛЕТОН) ВО ВРЕМЯ ЗАГРУЗКИ */
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px', padding: '10px 0' }}>
            <div style={{ width: '200px', height: '200px', background: 'rgba(255,255,255,0.1)', borderRadius: '8px', animation: 'pulse 1.5s infinite' }} />
            <div style={{ width: '80%', height: '20px', background: 'rgba(255,255,255,0.1)', borderRadius: '4px', animation: 'pulse 1.5s infinite' }} />
          </div>
        ) : (
          <>
            <QrCode className={styles.qrCode} data={displayUrl} /> 
            <Text className={styles.url} size="sm" weight="semibold">
              {displayUrl} 
            </Text>
          </>
        )}
        <Button
          className={styles.button}
          Icon={LinkIcon}
          onClick={onButtonClick}
          disabled={isLoading} // Блокируем кнопку, пока нет ссылки
          data-testid="modal_inviteLink"
        >
          {t("action.copy_link")}
        </Button>
      </Modal>
      <Toast
        open={toastOpen}
        onDismiss={onToastDismiss}
        autoDismiss={2000}
        Icon={CheckIcon}
      >
        {t("invite_modal.link_copied_toast")}
      </Toast>
    </>
  );
};